<template>
  <div>
    <!-- <div class="py-2">
      <h3 class="section-title">Take Selfie</h3>
      <p class="section-subtitle">
        Take a clear selfie to verify your identity.
      </p>
    </div> -->

    <div class="upload-wrap">
      <input ref="selfieInput" type="file" class="file-input" accept="image/*" capture="user" @change="onChange" />

      <div class="upload-card" :class="{ 'has-error': errors.selfie }">
        <div v-if="previewUrl" class="preview">
          <img :src="previewUrl" alt="Selfie preview" />
        </div>
        <div v-else-if="isCameraOpen" class="camera">
          <v-btn
            icon="mdi-close"
            size="small"
            variant="tonal"
            class="camera-close"
            @click="closeCamera"
          />
          <video ref="videoEl" autoplay playsinline muted />
          <canvas ref="canvasEl" class="camera-canvas" />
        </div>
        <div v-else class="placeholder">
          <v-icon size="22">mdi-camera-outline</v-icon>
          <div class="placeholder-text">No selfie selected</div>
        </div>

        <div class="actions">
          <v-btn
            :variant="isCameraOpen ? 'flat' : 'outlined'"
            :color="isCameraOpen ? 'primary' : undefined"
            :prepend-icon="primaryIcon"
            @click="handlePrimaryAction"
          >
            {{ primaryLabel }}
          </v-btn>
        </div>

        <div class="file-name">
          {{ selfie?.name || "No file selected" }}
        </div>
      </div>

      <div v-if="errors.selfie" class="field-error">{{ errors.selfie }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    selfie: File | null;
    errors: {
      selfie?: string;
    };
  }>(),
  {
    selfie: null,
    errors: () => ({}),
  },
);

const emit = defineEmits<{
  "update:selfie": [File | null];
}>();

const selfieInput = ref<HTMLInputElement | null>(null);
const previewUrl = ref<string | null>(null);
const videoEl = ref<HTMLVideoElement | null>(null);
const canvasEl = ref<HTMLCanvasElement | null>(null);

const mediaStream = ref<MediaStream | null>(null);
const cameraUnavailable = ref(false);

const supportsCamera = computed(() => {
  return typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
});
const canUseCamera = computed(() => supportsCamera.value && !cameraUnavailable.value);
const isCameraOpen = computed(() => Boolean(mediaStream.value));
const primaryLabel = computed(() => {
  if (canUseCamera.value) {
    if (isCameraOpen.value) return "Capture";
    if (props.selfie) return "Retake Selfie";
    return "Take Selfie";
  }

  if (props.selfie) return "Change File";
  return "Select File";
});
const primaryIcon = computed(() => {
  if (isCameraOpen.value) return "mdi-camera";
  return canUseCamera.value ? "mdi-camera" : "mdi-upload";
});

function revokePreview() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
}

function stopStream() {
  if (!mediaStream.value) return;
  mediaStream.value.getTracks().forEach((t) => t.stop());
  mediaStream.value = null;
}

function closeCamera() {
  stopStream();
}

watch(
  () => props.selfie,
  (file) => {
    revokePreview();
    if (file) {
      previewUrl.value = URL.createObjectURL(file);
    }
    if (file && isCameraOpen.value) {
      closeCamera();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  revokePreview();
  closeCamera();
});

async function openCamera() {
  if (!canUseCamera.value) return false;
  if (isCameraOpen.value) return true;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user" },
      audio: false,
    });
    mediaStream.value = stream;
    cameraUnavailable.value = false;

    if (videoEl.value) {
      videoEl.value.srcObject = stream;
      await videoEl.value.play().catch(() => undefined);
    }

    return true;
  } catch (error) {
    closeCamera();
    cameraUnavailable.value = true;
    return false;
  }
}

function pickUpload() {
  closeCamera();
  selfieInput.value?.click();
}

async function handlePrimaryAction() {
  if (canUseCamera.value) {
    if (isCameraOpen.value) {
      await captureFromCamera();
      return;
    }

    const opened = await openCamera();
    if (opened) return;
  }
  pickUpload();
}

function onChange(event: Event) {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files?.[0] || null;
  emit("update:selfie", file);
  if (input) input.value = "";
}

async function captureFromCamera() {
  if (!videoEl.value || !canvasEl.value) return;

  const video = videoEl.value;
  const canvas = canvasEl.value;

  const width = video.videoWidth || 720;
  const height = video.videoHeight || 720;

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.drawImage(video, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9);
  });

  if (!blob) return;

  const file = new File([blob], `selfie-${Date.now()}.jpg`, {
    type: "image/jpeg",
  });

  emit("update:selfie", file);
  closeCamera();
}
</script>

<style scoped>
.section-title {
  text-transform: capitalize;
}

.section-subtitle {
  margin: 0 0 14px;
  color: #73829a;
  font-size: 14px;
}

.upload-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-input {
  display: none;
}

.upload-card {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-card.has-error {
  border-color: #f04438;
}

.preview {
  width: 100%;
  height: 220px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #edf0f5;
  background: #f8fafc;
  display: grid;
  place-items: center;
}

.preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  max-height: 220px;
  padding: 20px 0;
}

.camera {
  width: 100%;
  height: 220px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #edf0f5;
  background: #0b1220;
  display: grid;
  place-items: center;
  position: relative;
}

.camera video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.camera-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.camera-canvas {
  display: none;
}

.placeholder {
  height: 220px;
  border-radius: 12px;
  border: 1px dashed #cbd5f5;
  background: #f8f9ff;
  display: grid;
  place-items: center;
  gap: 8px;
  color: #667085;
}

.placeholder-text {
  font-size: 13px;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.file-name {
  color: #5b677a;
  font-size: 13px;
}

.field-error {
  font-size: 12px;
  color: #b42318;
  margin-left: 4px;
}
</style>
